import React, { useRef, useState } from 'react';
import { FormInput } from '@/components/common/FormInput';
import { FormTextarea } from '@/components/common/FormTextarea';
import { PrimaryButton } from '@/components/common/PrimaryButton';
import { FormDatePicker } from '@/components/common/FormDatePicker';
import { ArrowRightToLine, X } from 'lucide-react';
import { useCreateEventMutation } from '@/store/api/eventApi';
import { toast } from 'react-toastify';
import { eventSchema } from '@/utils/validation';
import { useNavigate } from 'react-router-dom';

export function CreateEventPage() {
  const [createEvent, { isLoading }] = useCreateEventMutation();
  const [errors, setErrors] = useState<Record<string, string>>({});
  const fileInputRef = useRef<HTMLInputElement | null>(null);
  const [form, setForm] = useState({
    title: '',
    name: '',
    date: null as Date | null,
    // level: '',
    place: '',
    // issuingAuthority: '',
    description: '',
    file: null as File | null,
  });
  const navigate = useNavigate();
  const handleChange = (key: string, value: any) => {
    setForm((prev) => ({ ...prev, [key]: value }));
    setErrors((prev) => ({ ...prev, [key]: '' }));
  };
  const formatDate = (date: Date) => date.toLocaleDateString('en-CA');
  const handleSubmit = async () => {
    try {
      const result = eventSchema.safeParse(form);

      if (!result.success) {
        const fieldErrors: Record<string, string> = {};

        result.error.errors.forEach((err) => {
          const key = err.path[0] as string;
          fieldErrors[key] = err.message;
        });

        setErrors(fieldErrors);
        return;
      }

      setErrors({});

      const formData = new FormData();

      formData.append('title', form.title);
      formData.append('name', form.name);
      formData.append('date', form.date ? formatDate(form.date) : '');
      // formData.append('level', form.level);
      formData.append('place', form.place);
      formData.append('description', form.description);

      if (form.file) {
        formData.append('file', form.file);
      }

      await createEvent(formData).unwrap();
      toast.success('Event created successfully');
      setForm({
        title: '',
        name: '',
        date: null,
        // level: '',
        place: '',
        description: '',
        // issuingAuthority: ''
        file: null,
      });
      navigate('/event-list');
    } catch (err) {
      console.error(err);
      toast.error('Something went wrong');
    }
  };
  return (
    <div className="lg:p-4 bg-[#1F1F1F33]  text-white flex justify-center items-center">
      <div className="w-full max-w-[1480px] border border-white/10 rounded-xl p-4 bg-[#0b0b0f] shadow-xl">
        <div className="mb-6">
          <h2 className="font-24  font-bold">Create New Event</h2>
          <p className="font-16 text-[#A7A7A7] mt-0.5">Enter the basic information for Event</p>
        </div>

        <div className="grid md:grid-cols-2 gap-4">
          <div>
            <FormInput
              label="Event Title"
              required
              placeholder="Enter event title"
              value={form.title}
              onChange={(e) => handleChange('title', e.target.value)}
              error={!!errors.title}
            />
            {errors.title && <p className="text-sm text-red-500 mt-1">{errors.title}</p>}
          </div>
          <div>
            <FormInput
              label="Event Name"
              required
              placeholder="Enter Name"
              value={form.name}
              onChange={(e) => handleChange('name', e.target.value)}
              error={!!errors.name}
            />
            {errors.name && <p className="text-sm text-red-500 mt-1">{errors.name}</p>}
          </div>
          <div>
            <FormDatePicker
              label="Date"
              required
              value={form.date}
              onChange={(date) => handleChange('date', date)}
              error={!!errors.date}
            />
            {errors.date && <p className="text-sm text-red-500 mt-1">{errors.date}</p>}
          </div>
          {/* <div>
            <FormInput
              label="Level"
              required
              placeholder="Enter level"
              value={form.level}
              onChange={(e) => handleChange('level', e.target.value)}
              error={!!errors.level}
            />
            {errors.level && <p className="text-sm text-red-500 mt-1">{errors.level}</p>}
          </div> */}

          <div>
            <FormInput
              label="Event Place"
              required
              placeholder="Enter Event Place"
              value={form.place}
              onChange={(e) => handleChange('place', e.target.value)}
              error={!!errors.place}
            />
            {errors.place && <p className="text-sm text-red-500 mt-1">{errors.place}</p>}
          </div>
          {/* <div>
            <FormInput
              label="Issuing Authority"
              required
              placeholder="Enter Issuing Authority"
              value={form.issuingAuthority}
              onChange={(e) => handleChange('issuingAuthority', e.target.value)}
              error={!!errors.issuingAuthority}
            />
            {errors.issuingAuthority && (
              <p className="text-sm text-red-500 mt-1">{errors.issuingAuthority}</p>
            )}
          </div> */}
          <div>
            <label className="font-18 text-white font-medium">
              Event Certificate <span className="text-red-500">*</span>
            </label>

            <input
              ref={fileInputRef}
              type="file"
              accept="image/*"
              onChange={(e) => {
                const file = e.target.files?.[0] || null;
                handleChange('file', file);
              }}
              className="w-full bg-transparent border border-white/20 rounded-md px-3 py-2 text-sm text-white file:bg-[#6F72FF] file:text-white file:border-0 file:px-3 file:py-1 file:rounded-md"
            />
            {form.file && (
              <div className="relative w-fit mt-3">
                <img
                  src={URL.createObjectURL(form.file)}
                  alt="preview"
                  className="w-32 h-32 object-cover rounded-xl border border-white/20"
                />

                <button
                  type="button"
                  onClick={() => {
                    handleChange('file', null);
                    if (fileInputRef.current) {
                      fileInputRef.current.value = '';
                    }
                  }}
                  className="absolute -top-2 -right-2 bg-red-500 hover:bg-red-600 text-white w-6 h-6 rounded-full flex items-center justify-center text-sm shadow-lg"
                >
                  <X size={12} />
                </button>
              </div>
            )}

            {errors.file && <p className="text-sm text-red-500 mt-1">{errors.file}</p>}
          </div>

          <div className="md:col-span-2">
            <FormTextarea
              label="Event Description"
              required
              placeholder="Event Description"
              value={form.description}
              onChange={(e) => handleChange('description', e.target.value)}
              error={!!errors.description}
            />
            {errors.description && (
              <p className="text-sm text-red-500 mt-1">{errors.description}</p>
            )}
          </div>
        </div>

        <div className="flex justify-end mt-6">
          <PrimaryButton onClick={handleSubmit} disabled={isLoading}>
            {isLoading ? (
              <span className={`flex items-center gap-2 ${isLoading ? 'cursor-not-allowed' : ''}`}>
                <span className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></span>
                Creating...
              </span>
            ) : (
              <span className="flex items-center gap-2">
                Submit <ArrowRightToLine />
              </span>
            )}
          </PrimaryButton>
        </div>
      </div>
    </div>
  );
}
